import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { getV1Articles as getArticles } from '@/shared/api/__generated__/endpoints/articles/articles';
import { getV1Categories as getCategories } from '@/shared/api/__generated__/endpoints/categories/categories';
import { getV1Rubrics as getRubrics } from '@/shared/api/__generated__/endpoints/rubrics/rubrics';
import { getV1Search as getSearch } from '@/shared/api/__generated__/endpoints/search/search';
import { getV1TagsId as getTagsId } from '@/shared/api/__generated__/endpoints/tags/tags';

export const getMainArticlesQuery = ({
  key,
  category,
  rubric,
  limit = 10,
}: {
  key?: string;
  category?: string;
  rubric?: string;
  limit?: number;
}) =>
  queryOptions({
    queryKey: [key, category, rubric, limit],
    queryFn: async () => {
      const { data, status } = await getArticles({
        is_main: true,
        rubric_id: rubric ? [rubric] : undefined,
        ...(category && { category_ids: [category] }),
        limit,
        offset: 0,
      });
      return status === 200 ? (data.articles ?? []) : [];
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getInfinityArticles = ({
  key,
  category,
  rubric,
  tag,
  limit = 15,
  is_main,
  is_recommended,
}: {
  key?: string;
  category?: string;
  rubric?: string;
  tag?: string;
  limit?: number;
  is_main?: boolean;
  is_recommended?: boolean;
}) =>
  infiniteQueryOptions({
    queryKey: [key, category, rubric, tag, limit, is_main],
    queryFn: async ({ pageParam }) => {
      const { data, status } = await getArticles({
        is_main,
        rubric_id: rubric ? [rubric] : undefined,
        ...(category && { category_ids: [category] }),
        limit: limit,
        offset: limit * pageParam,
        tag,
        is_recommended,
      });
      const articles = status === 200 ? (data.articles ?? []) : [];
      return {
        articles: articles,
        lastPage: pageParam,
        hasNextPage: articles.length >= limit,
      };
    },
    getNextPageParam: (last) => (!last.hasNextPage ? null : last.lastPage + 1),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getArticlesQueryParams = ({
  key,
  category,
  rubric,
  tag,
  limit = 5,
  is_main,
  is_recommended,
}: {
  key?: string;
  category?: string;
  rubric?: string;
  tag?: string;
  limit?: number;
  is_main?: boolean;
  is_recommended?: boolean;
}) =>
  queryOptions({
    queryKey: [key, category, rubric, tag, limit, is_main],
    queryFn: async () => {
      const { data, status } = await getArticles({
        is_main,
        rubric_id: rubric ? [rubric] : undefined,
        ...(category && { category_ids: [category] }),
        limit,
        tag,
        is_recommended,
      });

      const articles = status === 200 ? (data.articles ?? []) : [];

      return articles;
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getInfinitySearchArticles = ({
  key,
  search,
  limit,
}: {
  key?: string;
  search: string;
  limit: number;
}) =>
  infiniteQueryOptions({
    queryKey: [key, search, limit],
    queryFn: async ({ pageParam }) => {
      const { data, status } = await getSearch({
        query: search,
        limit: limit,
        offset: limit * pageParam,
      });
      const articles = status === 200 ? (data.items ?? []) : [];
      return {
        articles: articles,
        lastPage: pageParam,
        hasNextPage: articles.length >= limit,
      };
    },
    getNextPageParam: (last) => (!last.hasNextPage ? null : last.lastPage + 1),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getRecommendedArticlesQuery = ({
  limit = 7,
}: {
  limit?: number;
}) =>
  queryOptions({
    queryKey: ['recommended-topics'],
    queryFn: async () => {
      const { data, status } = await getArticles({
        is_recommended: true,
        limit,
        offset: 0,
      });

      return status === 200 ? (data.articles ?? []) : [];
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getCategoriesQuery = () =>
  queryOptions({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, status } = await getCategories();
      return status === 200 ? (data ?? []) : [];
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getRubricsQuery = () =>
  queryOptions({
    queryKey: ['rubrics'],
    queryFn: async () => {
      const { data, status } = await getRubrics();
      return status === 200 ? (data ?? []) : [];
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getInfinityImportantArticles = ({
  key,
  limit,
}: {
  key?: string;
  limit: number;
}) =>
  infiniteQueryOptions({
    queryKey: [key, limit],
    queryFn: async ({ pageParam }) => {
      const { data, status } = await getArticles({
        is_hot_today: true,
        limit: limit,
        offset: limit * pageParam,
      });
      const articles = status === 200 ? (data.articles ?? []) : [];
      return {
        articles: articles,
        lastPage: pageParam,
        hasNextPage: articles.length >= limit,
      };
    },
    getNextPageParam: (last) => (!last.hasNextPage ? null : last.lastPage + 1),
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getImportantArticles = ({
  importance_level = 'tertiary',
  limit,
}: {
  importance_level?: string;
  limit: number;
}) =>
  queryOptions({
    queryKey: ['important', importance_level, limit],
    queryFn: async () => {
      const { data, status } = await getArticles({
        is_hot_today: true,
        importance_level: [importance_level],
        limit,
        offset: 0,
      });
      return status === 200 ? (data.articles ?? []) : [];
    },
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

export const getTagOptions = (tag: string) =>
  queryOptions({
    queryKey: ['tag', tag],
    queryFn: async () => {
      const response = await getTagsId(tag);

      if (response.status === 200) return response.data;

      return null;
    },
  });
